import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { fetchCache, imageCache, setInCache } from '../../../../../../utils/clearId8ImageCaches';
import sendRequest from '../../../../../../service/sendRequest';
import { getDemoImage } from '../../../../../../utils/id8ImagesRequests';
import Icon from '../../../../../../icons/Icon';
import { ICONS } from '../../../../../../icons';
import CommonLoader from '../../../../../../components/common-loader/CommonLoader';

import scss from './DemoImage.module.scss';

function DemoImage({ sessionId, idea, className, onClick, disabled, style = {}, onImageLoaded }) {
  const { t } = useTranslation('id8');
  const [imageUrl, setImageUrl] = useState(null);
  const [hasError, setHasError] = useState(false);

  const handleClick = () => {
    if (onClick && !disabled) {
      onClick();
    }
  };

  const handleError = () => {
    setImageUrl(null);
    setHasError(true);
  };

  useEffect(() => {
    if (!sessionId) return;

    if (imageCache.has(sessionId)) {
      setImageUrl(imageCache.get(sessionId));
      onImageLoaded?.(imageCache.get(sessionId));
      return;
    }

    const cachedPromise = fetchCache.get(sessionId);
    const fetchPromise =
      cachedPromise ||
      sendRequest(() => getDemoImage(sessionId), {
        onError: () => {
          fetchCache.delete(sessionId);
          setHasError(true);
        },
      }).then((response) => {
        const blobUrl = URL.createObjectURL(new Blob([response.data]));
        setInCache(imageCache, sessionId, blobUrl);
        return blobUrl;
      });

    setInCache(fetchCache, sessionId, fetchPromise);
    fetchPromise.then((url) => {
      setImageUrl(url);
      onImageLoaded?.(url);
    });
  }, [sessionId, onImageLoaded]);

  useEffect(() => {
    return () => {
      if (imageUrl && !Array.from(imageCache.values()).includes(imageUrl)) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [imageUrl]);

  if (!sessionId) return null;
  if (hasError)
    return (
      <div className={scss.errorWrapper}>
        <Icon icon={ICONS.Logo} className={scss.errorIcon} />
        <span className={scss.errorText}>{t('errorOccurred')}</span>
      </div>
    );
  if (!imageUrl) return <CommonLoader isLoading={true} />;

  return (
    <img
      src={imageUrl}
      alt={idea ? `${idea} illustration` : 'Image'}
      className={className}
      onClick={handleClick}
      role='presentation'
      loading='lazy'
      onError={handleError}
      style={style}
    />
  );
}

export default DemoImage;
